import { objectWithout } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import ConsoleIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { downloadBlob, inDebugMode, usePageTitle } from '../../Lib';

import { ApiReportType } from '@kaiizen/kai-pro-api';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { getDeoptimizedScan, getScanGroup, getScanTask, isGroupScoped, isTaskScoped, ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { PageLayout } from '../../Layout';
import {
  AllScanPhotosLegacy,
  GroupEditor,
  ProgressEditor,
  ProgressScanNavigator,
  ProjectMeasurementsModal,
  ScanLargePhotoViewer,
  ScanNavigator,
  ScanReview,
  ScopePrintPreview,
} from '../../Partials';
import {
  getLegacyExpandedTaskIdFromScanSearchParams,
  getScanUrl,
  getScanUrlParent,
  getValidatedScanUrlParts,
  ShowNotesProvider,
  useAutoSwitchOrgForResource,
  useElementSize,
  useNavigateToScan,
  useScanEditingContext,
} from '../../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';

import { type ScanFocusPanelPage, useNavigateToProject } from '../../State/kaiNavigation';
import { GenericFailurePage, GenericLoadingPage } from '../GenericPages';

const PANEL_CONTENT_STYLE = css`
  label: PANEL_CONTENT_STYLE;
  flex: 1;
  ${TYPOGRAPHY2.xSmall}
  background: var(--background2);
  width: 100%;
  display: flex;
  height: 100%;
  min-height: 0;
`;
const LEFT_PANEL_STYLE = css`
  label: LEFT_PANEL_STYLE;

  width: ${LAYOUT2.leftNavWidth}px;
  min-width: 250px;
  overscroll-behavior: contain;
  align-self: stretch;
`;

const MAIN_CONTENT_STYLE = css`
  label: MAIN_CONTENT_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;

  overflow: hidden;
  min-height: 0;
  min-width: 340px
`;

const DEBUG_BUTTON_STYLE = css`
  label: DEBUG_BUTTON_STYLE;
  padding: 12px;
  min-width: 44px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${COLORS2.alwaysDark.text.darkGray};
  &:hover {
    color: ${COLORS2.alwaysDark.text.white};
  }
`;

const MIDDLE_CONTENT_STYLE = css`
  label: MIDDLE_CONTENT_STYLE;
  flex: 1;
  display: flex;
  min-width: 0;
`;

export const AdminScanPage = ({ review = false, focusPanel, reportType, scanGroupId, scanTaskId, progress = false }: {
  review?: boolean;
  focusPanel?: ScanFocusPanelPage;
  reportType?: ApiReportType;
  scanGroupId?: string;
  scanTaskId?: string;
  progress?: boolean;
}) => {
  const showAllScanPhotos = focusPanel === 'photos';
  // scan-editing context
  const { scanId, scan, scanMutators, scanRoot, errorInfo, baselineScanRoot, project, property, isError, error } = useScanEditingContext();
  const { featureFlags } = useFeatureFlags();
  usePageTitle(property?.address ? `Scan · ${property.address}` : undefined);

  useAutoSwitchOrgForResource(
    { error: isError ? error : undefined, scanId },
  );

  // routing
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToProject = useNavigateToProject();
  const navigateToScan = useNavigateToScan();
  const [searchParams] = useSearchParams();
  const photoId = searchParams.get('photoId') || undefined;
  const isDebug = inDebugMode();
  const taskIdFromUrl = getLegacyExpandedTaskIdFromScanSearchParams(searchParams, scanTaskId);

  const rootElementRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(rootElementRef as any);

  // filter state
  const [filter, setFilter] = useState('');
  const [projectMeasurementsModalOpen, setProjectMeasurementsModalOpen] = useState(false);
  const filterRegex = useMemo(() => {
    if (!filter) return undefined;
    return new RegExp(filter.trim().replace(/\s+/g, '.*'), 'i');
  }, [filter]);

  const useRoomMeasurements = !!featureFlags.useRoomMeasurements;

  const openProjectMeasurementsModal = useCallback(
    () => {
      if (useRoomMeasurements) {
        setProjectMeasurementsModalOpen(true);
        return;
      }
      if (project?.id) {
        navigateToProject({ projectId: project.id });
      }
    },
    [navigateToProject, project, useRoomMeasurements],
  );
  const closeProjectMeasurementsModal = useCallback(
    () => setProjectMeasurementsModalOpen(false),
    [],
  );

  const [pendingRoomMeasurementsGroupId, setPendingRoomMeasurementsGroupId] = useState<string | null>(null);
  const consumePendingRoomMeasurementsOpen = useCallback(
    () => setPendingRoomMeasurementsGroupId(null),
    [],
  );
  const selectGroupFromProjectMeasurementsModal = useCallback(
    (groupId: string) => {
      closeProjectMeasurementsModal();
      navigateToScan({ scanId, groupId, progress });
      setPendingRoomMeasurementsGroupId(groupId);
    },
    [closeProjectMeasurementsModal, navigateToScan, progress, scanId],
  );

  // **************************************************************
  // Memoized Locally relevant data
  // **************************************************************

  const { scopedGroups, group, task, scopedTasks, scanUrlParts } = useMemo(() => {
    const scanUrlParts = getValidatedScanUrlParts({
      scanRoot,
      scanId,
      groupId: scanGroupId,
      taskId: scanTaskId,
      review,
      focusPanel,
      progress,
      scope: reportType === 'fullScope' ? true : false,
      punchList: reportType === 'punchlist' ? true : false,
      closeout: reportType === 'closeout' ? true : false,
      changeOrder: reportType === 'changeOrder' ? true : false,
    });
    const { groupId, taskId } = scanUrlParts;
    const scopedGroups = scanRoot?.groups?.filter(isGroupScoped) ?? [];
    const group = scanRoot && groupId ? getScanGroup(scanRoot, groupId) : undefined;
    const task = scanRoot && group && taskId ? getScanTask(scanRoot, group.id, taskId) : undefined;
    const scopedTasks = group?.tasks?.filter(isTaskScoped) ?? [];
    return { scopedGroups, group, task, scopedTasks, scanUrlParts };
  }, [
    progress,
    reportType,
    review,
    scanGroupId,
    scanRoot,
    scanId,
    scanTaskId,
    focusPanel,
  ]);

  const redirectDestination = useMemo(() => {
    // can't redirect if we're in print preview or if we don't have a scan or scan root
    if (reportType || !scan || !scanRoot) return null;

    // couldn't find the group or task in the url, so go up one level
    if ((scanGroupId && !group) || (scanTaskId && !task)) return getScanUrlParent(scanUrlParts);

    // the rest of the cases only apply if we're in final or progress scan type, so if we're not, don't redirect
    if (progress && !group) {
      const wantGroup: ScanGroupOptimized | undefined = scopedGroups[0];
      const wantTask = wantGroup?.tasks?.find(isTaskScoped);
      if (wantGroup !== group || wantTask !== task) {
        return { scanId, groupId: wantGroup?.id, taskId: wantTask?.id, progress: true };
      }
    }
  }, [
    group,
    progress,
    reportType,
    scan,
    scanGroupId,
    scanId,
    scanRoot,
    scanTaskId,
    scanUrlParts,
    scopedGroups,
    task,
  ]);

  useEffect(() => {
    if (!redirectDestination) return;
    const redirectUrl = getScanUrl(redirectDestination);
    if (location.pathname === redirectUrl) return;
    navigate(redirectUrl, { replace: true });
  }, [navigate, redirectDestination, location.pathname]);

  // **************************************************************
  // Functions
  // **************************************************************

  const exitLargePhotoViewer = useCallback(() => navigateToScan(objectWithout(scanUrlParts, 'photoId')), [navigateToScan, scanUrlParts]);
  const setCurrentGroupIdAndTaskId = useCallback((groupId: string | undefined, taskId: string | undefined) => {
    navigateToScan({ scanId, groupId, taskId, progress });
  }, [scanId, progress, navigateToScan]);

  const projectId = project?.id;
  const onBack = useCallback(() => projectId && navigateToProject({ projectId }), [navigateToProject, projectId]);
  const approveCloseout = useCallback(async () => {
    if (!scan) return;
    await scanMutators.update(scan.id, {
      scanLifecycleStatus: 'approved',
    });
    navigateToProject({ projectId: scan.propertyProjectId });
  }, [navigateToProject, scan, scanMutators]);

  const requestCloseoutEdit = useCallback(async () => {
    if (!scan) return;
    await scanMutators.update(scan.id, {
      scanLifecycleStatus: 'correctionsRequested',
    });
  }, [scan, scanMutators]);

  // **************************************************************
  // First Returns
  // **************************************************************
  if (redirectDestination) return <GenericLoadingPage loadingMessage='Redirecting...' />;
  if (errorInfo) return <GenericFailurePage errorInfo={errorInfo} />;
  if (!scan || !scanRoot || !project || !property) {
    return (
      <GenericLoadingPage
        loadingMessage={!scan
          ? 'Loading scan...'
          : !scanRoot
          ? 'Loading scan data...'
          : !project
          ? 'Loading property project...'
          : !property
          ? 'Loading organization property...'
          : 'Loading...'}
      />
    );
  }

  // **************************************************************
  // Debug Buttons
  // **************************************************************
  const debugButtons = isDebug && (
    <>
      <div
        key='download-deoptimized-scan'
        onClick={() => {
          const deoptimizedScan = getDeoptimizedScan(scanRoot);
          const dataStr = JSON.stringify(deoptimizedScan, null, 2);
          downloadBlob(new Blob([dataStr], { type: 'application/json' }), `${scan.name || 'scan'}-deoptimized.json`);
        }}
        css={DEBUG_BUTTON_STYLE}
        title='Download deoptimized scan as JSON'
      >
        <DownloadIcon fontSize='medium' />
      </div>
      <div
        key='log-deoptimized-scan'
        onClick={() => {
          const deoptimizedScan = getDeoptimizedScan(scanRoot);
          console.log('Deoptimized scanRoot:', deoptimizedScan);
          (window as any).scanRoot = scanRoot;
          (window as any).deoptimizedScan = deoptimizedScan;
          console.log('Template:', scanRoot.template);
          (window as any).template = scanRoot.template;
          console.log(`window.scan = deoptimizedScan\nwindow.deoptimizedScan = deoptimizedScan`);
        }}
        css={DEBUG_BUTTON_STYLE}
        title='Log deoptimized scan to console and set window.scan'
      >
        <ConsoleIcon fontSize='medium' />
      </div>
    </>
  );

  const pageHeaderButtons = debugButtons;

  // **************************************************************
  // Document Pages (e.g. Scope, Change Order, Punchlist & Closeout)
  // **************************************************************
  if (reportType) {
    return (
      <PageLayout
        key={'scan'}
        title={property?.address || scan.name}
        onBack={onBack}
        headerButtons={pageHeaderButtons}
      >
        {<ScopePrintPreview scanId={scanId} scanName={scan.name || 'scan'} reportType={reportType} />}
      </PageLayout>
    );
  }

  // **************************************************************
  // Scan Navigator Left Panel
  // **************************************************************
  const showLeftPanel = width === 0 || width >= 1024; // width === 0 happens sometimes when we can't get the size of the window
  const leftPanel = (
    <div css={LEFT_PANEL_STYLE}>
      {progress
        ? (
          <ProgressScanNavigator
            scanId={scanId}
            scopedGroups={scopedGroups}
            currentGroup={group}
            currentTask={task}
          />
        )
        : (
          <ScanNavigator
            filter={filter}
            filterRegex={filterRegex}
            group={group}
            onOpenProjectMeasurements={openProjectMeasurementsModal}
            review={review}
            setFilter={setFilter}
            showAllScanPhotos={showAllScanPhotos}
          />
        )}
    </div>
  );

  // **************************************************************
  // Main Content (Scan Editor and Progress Editor)
  // **************************************************************
  const showFullScreenPhotoViewer = !!photoId;

  let mainContent: React.ReactNode | undefined = undefined;
  if (progress) {
    mainContent = (
      <ProgressEditor
        group={group}
        scopedGroups={scopedGroups}
        task={task}
        scopedTasks={scopedTasks}
        setCurrentGroupIdAndTaskId={setCurrentGroupIdAndTaskId}
      />
    );
  } else if (group) {
    mainContent = (
      <GroupEditor
        autoOpenRoomMeasurementsGroupId={pendingRoomMeasurementsGroupId}
        baselineGroup={baselineScanRoot?.groupsById[group.id]}
        filterRegex={filterRegex}
        group={group}
        initialExpandedTaskId={taskIdFromUrl}
        onAutoOpenRoomMeasurementsConsumed={consumePendingRoomMeasurementsOpen}
        onOpenProjectMeasurements={openProjectMeasurementsModal}
      />
    );
  } else if (showAllScanPhotos) {
    mainContent = (
      <AllScanPhotosLegacy
        filterRegex={filterRegex}
      />
    );
  } else if (review) mainContent = <ScanReview filterRegex={filterRegex} />;

  // **************************************************************
  // LAYOUT
  // **************************************************************
  return (
    <ShowNotesProvider>
      <PageLayout
        key={'scan'}
        title={property?.address || scan.name}
        onBack={onBack}
        headerButtons={pageHeaderButtons}
      >
        {useRoomMeasurements && (
          <ProjectMeasurementsModal
            listedSqft={property.customerMetadata?.squareFeet ?? null}
            onClose={closeProjectMeasurementsModal}
            onSelectGroup={selectGroupFromProjectMeasurementsModal}
            scanRoot={scanRoot}
            visible={projectMeasurementsModalOpen}
          />
        )}
        <div css={PANEL_CONTENT_STYLE} ref={rootElementRef}>
          {showLeftPanel && leftPanel}
          <div css={[MIDDLE_CONTENT_STYLE, showFullScreenPhotoViewer && css`display: none;`]}>
            <div css={MAIN_CONTENT_STYLE}>{mainContent}</div>
          </div>
          {showFullScreenPhotoViewer && <ScanLargePhotoViewer closePhotos={exitLargePhotoViewer} />}
        </div>
      </PageLayout>
    </ShowNotesProvider>
  );
};
